import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    CheckCircle, XCircle, Clock, DollarSign, Calendar,
    TrendingUp, TrendingDown, Minus, FileText, Download
} from 'lucide-react';

export default function CloseoutHistory() {
    const { user } = useAuth();
    const [closeouts, setCloseouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCloseout, setSelectedCloseout] = useState(null);
    const [managerNotes, setManagerNotes] = useState('');
    const [approving, setApproving] = useState(false);

    useEffect(() => {
        loadCloseouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.store_id]);

    const loadCloseouts = async () => {
        try {
            setLoading(true);
            const response = await api.get('/closeout/history/list', {
                params: { store_id: user?.store_id, limit: 50 }
            });
            setCloseouts(response.data);
        } catch (error) {
            toast.error('Failed to load closeout history');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (closeoutId, approved) => {
        try {
            setApproving(true);
            await api.post('/closeout/approve', {
                closeout_id: closeoutId,
                approved: approved,
                manager_notes: managerNotes
            });
            toast.success(approved ? 'Closeout approved' : 'Closeout rejected');
            setSelectedCloseout(null);
            setManagerNotes('');
            loadCloseouts();
        } catch (error) {
            toast.error('Failed to process approval');
        } finally {
            setApproving(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            completed: 'bg-green-500/20 text-green-400 border-green-500/30',
            approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        return styles[status] || styles.pending;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `$${(amount || 0).toFixed(2)}`;
    };

    if (loading) {
        return (
            <Layout title="Closeout History">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Closeout History">
            <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Closeout History</h1>
                        <p className="text-slate-400">View and manage daily closeouts</p>
                    </div>
                    <Button
                        onClick={loadCloseouts}
                        variant="outline"
                        className="border-slate-700 text-slate-300"
                    >
                        Refresh
                    </Button>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-400">Total Closeouts</p>
                                    <p className="text-2xl font-bold text-white">{closeouts.length}</p>
                                </div>
                                <FileText className="h-8 w-8 text-cyan-500/50" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-400">Pending Approval</p>
                                    <p className="text-2xl font-bold text-yellow-400">
                                        {closeouts.filter(c => c.status === 'completed').length}
                                    </p>
                                </div>
                                <Clock className="h-8 w-8 text-yellow-500/50" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-400">Approved</p>
                                    <p className="text-2xl font-bold text-emerald-400">
                                        {closeouts.filter(c => c.status === 'approved').length}
                                    </p>
                                </div>
                                <CheckCircle className="h-8 w-8 text-emerald-500/50" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-400">Total Sales</p>
                                    <p className="text-2xl font-bold text-cyan-400">
                                        {formatCurrency(closeouts.reduce((sum, c) => sum + (c.net_sales || 0), 0))}
                                    </p>
                                </div>
                                <DollarSign className="h-8 w-8 text-cyan-500/50" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Closeouts Table */}
                <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader>
                        <CardTitle className="text-white">All Closeouts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-700">
                                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Cashier</th>
                                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Net Sales</th>
                                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Cash Over/Short</th>
                                        <th className="text-center py-3 px-4 text-slate-400 font-medium">Status</th>
                                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {closeouts.map((closeout) => (
                                        <tr key={closeout.id} className="border-b border-border hover:bg-muted/50">
                                            <td className="py-3 px-4 text-white">
                                                {formatDate(closeout.created_at)}
                                            </td>
                                            <td className="py-3 px-4 text-slate-300">
                                                {closeout.created_by_name || closeout.created_by}
                                            </td>
                                            <td className="py-3 px-4 text-right text-white">
                                                {formatCurrency(closeout.net_sales)}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <span className={closeout.cash_over_short > 0 ? 'text-green-400' : closeout.cash_over_short < 0 ? 'text-red-400' : 'text-slate-400'}>
                                                    {closeout.cash_over_short > 0 ? '+' : ''}{formatCurrency(closeout.cash_over_short)}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <Badge variant="outline" className={getStatusBadge(closeout.status)}>
                                                    {closeout.status}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setSelectedCloseout(closeout)}
                                                        className="border-slate-700 text-slate-300"
                                                    >
                                                        View
                                                    </Button>
                                                    {closeout.status === 'completed' && (user?.role === 'admin' || user?.role === 'manager') && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleApprove(closeout.id, true)}
                                                                disabled={approving}
                                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                            >
                                                                <CheckCircle className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleApprove(closeout.id, false)}
                                                                disabled={approving}
                                                                variant="outline"
                                                                className="border-red-700 text-red-400 hover:bg-red-900/20"
                                                            >
                                                                <XCircle className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                {/* Selected Closeout Detail Modal */}
                {selectedCloseout && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <Card className="bg-card border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-white">Closeout Details</CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedCloseout(null)}
                                    className="text-slate-400"
                                >
                                    ✕
                                </Button>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-slate-400">Date</p>
                                        <p className="text-white">{formatDate(selectedCloseout.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Status</p>
                                        <Badge variant="outline" className={getStatusBadge(selectedCloseout.status)}>
                                            {selectedCloseout.status}
                                        </Badge>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Cashier</p>
                                        <p className="text-white">{selectedCloseout.created_by_name || selectedCloseout.created_by}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Transactions</p>
                                        <p className="text-white">{selectedCloseout.total_transactions}</p>
                                    </div>
                                </div>

                                <div className="border-t border-slate-700 pt-4">
                                    <h3 className="text-white font-medium mb-2">Sales Summary</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-slate-400">Gross Sales</p>
                                            <p className="text-white">{formatCurrency(selectedCloseout.gross_sales)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-400">Net Sales</p>
                                            <p className="text-cyan-400 font-medium">{formatCurrency(selectedCloseout.net_sales)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-400">Cash Sales</p>
                                            <p className="text-white">{formatCurrency(selectedCloseout.cash_expected)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-400">Card Sales</p>
                                            <p className="text-white">{formatCurrency(selectedCloseout.card_sales)}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-700 pt-4">
                                    <h3 className="text-white font-medium mb-2">Cash Reconciliation</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-slate-400">Expected Cash</p>
                                            <p className="text-white">{formatCurrency(selectedCloseout.cash_expected)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-400">Actual Cash</p>
                                            <p className="text-white">{formatCurrency(selectedCloseout.cash_actual)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-400">Over/Short</p>
                                            <p className={selectedCloseout.cash_over_short >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                {selectedCloseout.cash_over_short >= 0 ? '+' : ''}{formatCurrency(selectedCloseout.cash_over_short)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {selectedCloseout.notes && (
                                    <div className="border-t border-slate-700 pt-4">
                                        <p className="text-sm text-slate-400">Notes</p>
                                        <p className="text-white">{selectedCloseout.notes}</p>
                                    </div>
                                )}

                                {selectedCloseout.status === 'completed' && (user?.role === 'admin' || user?.role === 'manager') && (
                                    <div className="border-t border-slate-700 pt-4 space-y-4">
                                        <div>
                                            <label className="text-sm text-slate-400">Manager Notes</label>
                                            <textarea
                                                value={managerNotes}
                                                onChange={(e) => setManagerNotes(e.target.value)}
                                                className="w-full h-20 bg-background border-input text-foreground rounded-md p-2 mt-1"
                                                placeholder="Add approval notes..."
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => handleApprove(selectedCloseout.id, true)}
                                                disabled={approving}
                                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                Approve
                                            </Button>
                                            <Button
                                                onClick={() => handleApprove(selectedCloseout.id, false)}
                                                disabled={approving}
                                                variant="outline"
                                                className="flex-1 border-red-700 text-red-400 hover:bg-red-900/20"
                                            >
                                                <XCircle className="h-4 w-4 mr-2" />
                                                Reject
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </Layout>
    );
}
